import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useBoolean } from '@fluentui/react-hooks';
import {
  DefaultButton,
  IconButton,
  Label,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { Person } from '../../../models/Entities/Person/Person';
import { PersonTypeEnum } from '../../../models/Entities/Person/PersonCommands';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import PersonFormComponent from '../PersonForm/Person.Form.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ComponentFunctions from './Staff.List.Functions';

// styles
import './Staff.List.Styles.css';

const StaffListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();
  const navigate = useNavigate();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedPersonId, setSelectedPersonId] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    props.GetAllPersons();
  }, []);

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filtered = props.persons.filter((person: Person) => {
    if (normalizedSearchText === '') return true;

    return [person.businessName, person.firstName, person.lastName, person.activity, person.nationality]
      .filter((value) => !!value)
      .some((value) => (value as string).toLowerCase().includes(normalizedSearchText));
  });

  // events
  const handleOpenCreate = () => {
    setSelectedPersonId(undefined);
    openForm();
  };

  const handleEdit = (personId: number) => {
    setSelectedPersonId(personId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedPersonId(undefined);
    dismissForm();
  };

  const handleViewDetails = (person: Person) => {
    navigate('/staff/' + person.id);
  };

  const handleSearch = (newValue?: string) => {
    setSearchText(newValue || '');
  };

  const handleRefresh = () => {
    props.GetAllPersons({ PageNumber: 1, PageSize: 100 });
  };

  //#region Subcontent definition

  const Searchbox = (
    <SearchBox
      placeholder={t('staffList.searchPlaceholder', { defaultValue: 'Buscar miembro del staff' })}
      underlined={false}
      onChange={(
        event?: React.ChangeEvent<HTMLInputElement> | undefined,
        newValue?: string | undefined
      ) => handleSearch(newValue || '')}
      onClear={() => handleSearch('')}
    />
  );

  const Controls = (
    <>
      <div className="StaffControls">
        <div className="StaffActionButtons">
          <PrimaryButton
            text={t('staffList.button.addStaff', { defaultValue: 'Agregar miembro' })}
            iconProps={ComponentFunctions.AddIcon}
            onClick={handleOpenCreate}
            className="actionButton"
          />
          <DefaultButton
            iconProps={ComponentFunctions.RefreshIcon}
            title={t('staffList.button.refresh', { defaultValue: 'Actualizar' })}
            ariaLabel={t('staffList.button.refresh', { defaultValue: 'Actualizar' })}
            onClick={handleRefresh}
            disabled={props.isLoading}
          />
        </div>
        {Searchbox}
      </div>
      <Separator />
    </>
  );

  const PrincipalErrorMessage = props.failOnLoading && (
    <MessageComponent
      message={t('staffList.errorMessage.error', { defaultValue: 'No fue posible obtener el staff' })}
      subMessage={props.error?.ErrorMessage || ''}
      type={MessageBarType.error}
      itemList={props.error?.Errors || []}
    />
  );

  const BrokenIdentifiersMessage = props.hasBrokenIdentifiers && (
    <>
      <MessageComponent
        message={t('staffList.identifiers.brokenMessage', {
          defaultValue: 'Se detectaron personas sin identificador único'
        })}
        subMessage={t('staffList.identifiers.brokenSubMessage', {
          defaultValue: 'Ejecute la corrección de identificadores para regularizarlas'
        })}
        type={MessageBarType.warning}
      />
      <DefaultButton
        text={
          props.isFixingIdentifiers
            ? t('staffList.identifiers.fixing', { defaultValue: 'Corrigiendo...' })
            : t('staffList.identifiers.fix', { defaultValue: 'Corregir identificadores' })
        }
        onClick={() => props.FixPersonIdentifiers()}
        disabled={props.isFixingIdentifiers}
        className="optionButton"
      />
    </>
  );

  const EmptyMessage = !props.isLoading && filtered.length === 0 && (
    <MessageComponent
      message={t('staffList.empty', { defaultValue: 'No hay miembros del staff registrados' })}
      type={MessageBarType.info}
    />
  );

  const TableOfContent = props.isLoadingSuccessfully && filtered.length > 0 && (
    <table className="Table">
      <thead>
        <tr>
          <th>{t('staffList.parameters.id', { defaultValue: 'ID' })}</th>
          <th>{t('staffList.parameters.name', { defaultValue: 'Nombre' })}</th>
          <th>{t('staffList.parameters.personType', { defaultValue: 'Tipo' })}</th>
          <th>{t('staffList.parameters.activity', { defaultValue: 'Actividad' })}</th>
          <th>{t('staffList.parameters.nationality', { defaultValue: 'Nacionalidad' })}</th>
          <th style={{ width: '180px' }}>
            {t('staffList.parameters.options', { defaultValue: 'Acciones' })}
          </th>
        </tr>
      </thead>
      <tbody>
        {filtered.map((person: Person) => (
          <tr key={person.id} className="TableBodyRow">
            <td className="primary">{person.id}</td>
            <td className="StaffPrimaryCell" onClick={() => handleViewDetails(person)}>
              <Label className="upper">{CommonFunctions.GetPersonDisplayName(person)}</Label>
            </td>
            <td>
              <span
                className={
                  person.personType === PersonTypeEnum.JuridicalPerson
                    ? 'StaffTypeBadge juridical'
                    : 'StaffTypeBadge'
                }
              >
                {person.personType === PersonTypeEnum.JuridicalPerson
                  ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' })
                  : t('persons.personType.natural', { defaultValue: 'Persona física' })}
              </span>
            </td>
            <td>
              <Text>{person.activity || '-'}</Text>
            </td>
            <td>
              <Text>{person.nationality || '-'}</Text>
            </td>
            <td style={{ width: '180px' }}>
              <IconButton
                iconProps={ComponentFunctions.ViewIcon}
                title={t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' })}
                ariaLabel={t('staffList.parameters.buttons.detailsButton', { defaultValue: 'Ver ficha' })}
                onClick={() => handleViewDetails(person)}
              />
              <IconButton
                iconProps={ComponentFunctions.EditIcon}
                title={t('staffList.parameters.buttons.modifyButton', { defaultValue: 'Modificar' })}
                ariaLabel={t('staffList.parameters.buttons.modifyButton', { defaultValue: 'Modificar' })}
                onClick={() => handleEdit(person.id)}
              />
              <div className="danger">
                <ConfirmationButtonComponent
                  ButtonId={`StaffDeleteButton-${person.id}`}
                  ButtonIconName={'Delete'}
                  ButtonText={''}
                  ButtonCssClass={''}
                  ConfirmationButtonText={t('staffList.delete.confirm', { defaultValue: 'Eliminar' })}
                  ConfirmationButtonCssClass={'deleteButton'}
                  CancelButtonText={t('staffList.delete.cancel', { defaultValue: 'Cancelar' })}
                  CancelButtonCssClass={''}
                  Title={t('staffList.delete.title', { defaultValue: 'Eliminar miembro del staff' })}
                  Description={t('staffList.delete.description', {
                    defaultValue: '¿Desea eliminar al miembro del staff seleccionado?'
                  })}
                  ElementTextHeader={t('staffList.delete.element', { defaultValue: 'Miembro' })}
                  ElementTextInformation={CommonFunctions.GetPersonDisplayName(person)}
                  onConfirmation={() => props.DeletePerson(person.id)}
                  onCancel={() => undefined}
                  isOperationExecuting={props.isDeleting || false}
                  isOperationExecutedWithError={props.failOnDeleting || false}
                  OperationError={props.error}
                />
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const Loading = props.isLoading && (
    <div>
      <Spinner
        label={t('staffList.loading', { defaultValue: 'Cargando staff...' })}
        ariaLive="assertive"
        labelPosition="top"
      />
    </div>
  );

  //#endregion

  return (
    <>
      {Controls}
      {PrincipalErrorMessage}
      {BrokenIdentifiersMessage}
      {EmptyMessage}
      {TableOfContent}
      {Loading}
      <PersonFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        personId={selectedPersonId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(StaffListComponent as any);
